import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { venueUrl } from '../tenant';
import EventsModule from '../events/EventsModule';
import GruposRrpp from '../admin/GruposRrpp';
import GruposLotes from '../admin/GruposLotes';
import TpvMenus from '../admin/TpvMenus';
import Analitica from '../admin/Analitica';
import EmailMkt from '../admin/EmailMkt';
import Puntos from '../admin/Puntos';
import Contable from '../admin/Contable';
import Galeria from '../admin/Galeria';
import DatosTitular from '../admin/DatosTitular';
import Sessions from '../admin/Sessions';
import MiWeb from '../admin/MiWeb';
import Inventario from '../admin/Inventario';
import IntegracionPagos from '../admin/IntegracionPagos';
import Facturacion from '../admin/Facturacion';
import MiPlan from '../admin/MiPlan';
import { usePlan, refrescarPlan, habilitada, EtiquetaPlan, PlanBloqueado } from '../components/Plan';
import styles from './Panel.module.css';

const GROUPS = [
  {
    title: 'Eventos',
    items: [
      { key: 'eventos', label: 'EVENTOS' },
      { key: 'analitica', label: 'ANALÍTICA', funcion: 'analitica' },
      { key: 'grupos', label: 'GRUPOS RRPP' },
      { key: 'grupos-lotes', label: 'GRUPOS DE LOTES' },
      { key: 'cajas', label: 'CAJAS TPV', funcion: 'tpv' },
    ],
  },
  {
    title: 'Gestión del venue',
    items: [
      { key: 'contable', label: 'CONTABLE', funcion: 'administracion' },
      { key: 'pagos', label: 'INTEGRACIÓN DE PAGOS' },
      // Al lado de pagos porque son la misma decisión vista de dos lados: quién cobra es
      // quien tiene que facturar.
      { key: 'facturacion', label: 'FACTURACIÓN ARCA', funcion: 'arca' },
      { key: 'inventario', label: 'INVENTARIO', funcion: 'inventario' },
      { key: 'galeria', label: 'GALERÍA', funcion: 'galeria' },
      { key: 'sessions', label: 'CANAL DE YOUTUBE', funcion: 'youtube' },
      { key: 'emailmkt', label: 'EMAIL MKT', funcion: 'marketing' },
      { key: 'puntos', label: 'SIST. PUNTOS', funcion: 'puntos' },
      { key: 'miweb', label: 'MI WEB', funcion: 'web' },
    ],
  },
  {
    title: 'Cuenta',
    items: [
      { key: 'miplan', label: 'MI PLAN' },
      { key: 'titular', label: 'DATOS DEL TITULAR' },
      { key: 'seguridad', label: 'SEGURIDAD' },
    ],
  },
];
const SECTIONS = GROUPS.flatMap(g => g.items);

// Banner de verificación de email: aparece mientras la org está 'pending'. Sin
// verificar, el backend bloquea toda acción operativa (403). Al verificar, guarda
// el token fresco (orgStatus='trial') y recarga → desaparece y se desbloquea todo.
// Banner del trial: muestra el progreso (QR usados / límite + días) y, si se agotó
// (1000 QR o 30 días), avisa que hay que pasar a un plan para seguir generando.
function TrialBanner() {
  const { user } = useAuth();
  const t = user?.trial;
  if (!t?.inTrial) return null;
  const venc = t.endsAt ? new Date(t.endsAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) : null;
  const color = t.frozen ? 'var(--red-400)' : '#d4af37';
  const bg = t.frozen ? 'rgba(200,16,46,0.08)' : 'rgba(212,175,55,0.08)';
  return (
    <div style={{ border: `1px solid ${color}55`, background: bg, borderRadius: 6, padding: '16px 20px', marginBottom: 22 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color, fontSize: 14 }}>
        {t.frozen ? 'Se agotó tu prueba gratis' : 'Estás en tu prueba gratis'}
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 4 }}>
        {t.frozen
          ? <>Usaste <b style={{ color: '#fff' }}>{t.qrUsed}</b> de {t.qrLimit} entradas de prueba{venc ? ` (vencía el ${venc})` : ''}. Para seguir generando entradas, elegí un plan desde <b style={{ color: '#fff' }}>Mi plan</b>. Lo que ya generaste y el escaneo en la puerta siguen funcionando.</>
          : <>Podés generar hasta <b style={{ color: '#fff' }}>{t.qrLimit}</b> entradas gratis{venc ? ` o hasta el ${venc}` : ''} (lo que ocurra primero). Llevás <b style={{ color: '#fff' }}>{t.qrUsed}</b>.</>}
      </div>
    </div>
  );
}

// Banner de cargo por evento vencido e impago: bloquea operar hasta regularizar.
function ChargeBanner() {
  const { user } = useAuth();
  if (!user?.hasOverdueCharge || user?.orgStatus === 'expired') return null; // el de suscripción ya cubre ese caso
  return (
    <div style={{ border: '1px solid rgba(200,16,46,0.5)', background: 'rgba(200,16,46,0.08)', borderRadius: 6, padding: '16px 20px', marginBottom: 22 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--red-400)', fontSize: 14 }}>Tenés un cargo vencido impago</div>
      <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 4 }}>
        El cargo por servicio de un evento venció sin pagarse. Podés ver tus datos y las entradas ya vendidas se siguen escaneando, pero <b style={{ color: '#fff' }}>no podés crear eventos ni vender</b> hasta regularizarlo. En <b style={{ color: '#fff' }}>Mi plan</b> tenés el detalle, los datos para transferir y el botón para avisarnos que pagaste.
      </div>
    </div>
  );
}

// Banner de suscripción vencida: el boliche ve sus datos pero no puede operar.
// En Fase 1 el pago/reactivación es manual (super-admin) → acá solo informa.
function ExpiredBanner() {
  const { user } = useAuth();
  if (user?.orgStatus !== 'expired') return null;
  return (
    <div style={{ border: '1px solid rgba(200,16,46,0.5)', background: 'rgba(200,16,46,0.08)', borderRadius: 6, padding: '16px 20px', marginBottom: 22 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--red-400)', fontSize: 14 }}>Tu suscripción venció</div>
      <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 4 }}>
        Podés ver tus datos y las entradas ya vendidas se siguen escaneando en la puerta con normalidad. Pero <b style={{ color: '#fff' }}>no podés crear eventos ni vender</b> hasta renovar. Escribinos para reactivar tu cuenta.
      </div>
    </div>
  );
}

function VerifyBanner({ api }) {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  if (user?.orgStatus !== 'pending') return null;
  async function verify() {
    setMsg(''); setBusy(true);
    try {
      const r = await api.post('/api/auth/verify-email', { code: code.trim() });
      if (r.token) { localStorage.setItem('tk_token', r.token); window.location.reload(); }
    } catch (e) { setMsg(e.message || 'Código inválido'); } finally { setBusy(false); }
  }
  async function resend() {
    setMsg('');
    try { await api.post('/api/auth/resend-code', {}); setMsg('Te reenviamos el código por email.'); }
    catch (e) { setMsg(e.message || 'No se pudo reenviar'); }
  }
  return (
    <div style={{ border: '1px solid rgba(212,175,55,0.5)', background: 'rgba(212,175,55,0.08)', borderRadius: 6, padding: '16px 20px', marginBottom: 22 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#d4af37', fontSize: 14 }}>Verificá tu email para empezar a operar</div>
      <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 4 }}>Te enviamos un código a <b style={{ color: '#fff' }}>{user?.email}</b>. Ingresalo para activar tu cuenta. Mientras tanto no podés crear eventos ni vender.</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <input className="lb-input" style={{ width: 140, letterSpacing: '0.2em', textAlign: 'center', fontFamily: 'monospace' }} value={code} inputMode="numeric" maxLength={6} placeholder="000000" onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={verify} disabled={busy || code.length !== 6}>{busy ? 'Verificando…' : 'Verificar'}</button>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={resend}>Reenviar código</button>
        {msg && <span style={{ fontSize: 12, color: 'var(--ink-700)' }}>{msg}</span>}
      </div>
    </div>
  );
}

// Aviso de aumento de precios programado (30 días). Lee /api/org/pricing y solo se
// muestra si el boliche tiene un aumento futuro que todavía no entró en vigencia.
function PriceNoticeBanner({ api }) {
  const [up, setUp] = useState(null);
  useEffect(() => { api.get('/api/org/pricing').then(d => setUp(d?.upcoming || null)).catch(() => {}); }, []); // eslint-disable-line
  if (!up) return null;
  const money = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString('es-AR'));
  const fecha = up.effective_at ? new Date(up.effective_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  return (
    <div style={{ border: '1px solid rgba(212,175,55,0.5)', background: 'rgba(212,175,55,0.08)', borderRadius: 6, padding: '16px 20px', marginBottom: 22 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#d4af37', fontSize: 14 }}>Actualización de precios desde el {fecha}</div>
      <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 6, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <span>Por entrada: <b style={{ color: '#fff' }}>{money(up.qr_unit)}</b></span>
        <span>Abono Pro: <b style={{ color: '#fff' }}>{money(up.plan_pro)}</b>/mes</span>
        <span>Abono Full: <b style={{ color: '#fff' }}>{money(up.plan_full)}</b>/mes</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 6 }}>Los cargos ya emitidos no cambian.</div>
    </div>
  );
}

export default function OwnerPanel() {
  const { user, logout } = useAuth();
  const api = useApi();
  // Qué habilita el plan: el menú marca lo que no y la sección muestra el porqué.
  const plan = usePlan();
  // DÓNDE ESTÁS PARADO VIVE EN LA URL (?s=contable&ev=…&t=ventas), no en memoria.
  //
  // Antes, refrescar en cualquier pantalla te devolvía a la lista de eventos: con una
  // noche en curso eso es perder el lugar justo cuando no hay tiempo. Guardarlo en la URL
  // además hace andar el botón "atrás" del navegador y permite dejar una pestaña abierta
  // en la pantalla que mirás siempre.
  const [params, setParams] = useSearchParams();
  const section = SECTIONS.some(s => s.key === params.get('s')) ? params.get('s') : 'eventos';
  const [menuOpen, setMenuOpen] = useState(false);
  const [webSlug, setWebSlug] = useState(null);
  // Cambiar de sección limpia lo que era del evento abierto (su id y su pestaña).
  const go = (key) => {
    setParams(key === 'eventos' ? {} : { s: key }, { replace: true });
    setMenuOpen(false);
  };

  // Slug del venue para el acceso rápido a la web pública.
  useEffect(() => { api.get('/api/org').then(o => setWebSlug(o.slug)).catch(() => {}); }, []); // eslint-disable-line

  return (
    <div className={styles.layout}>
      {menuOpen && <div className={styles.backdrop} onClick={() => setMenuOpen(false)} />}
      {/* Sidebar */}
      <aside className={`${styles.sidebar} ${menuOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.sidebarBrand}>
          <img src="/assets/tukipass-icon.png" alt="Tukipass" className={styles.brandMark} style={{ objectFit: 'cover' }} />
          <div>
            <div className={styles.brandName}>TUKIPRO</div>
            <div className={styles.brandRole}>DUEÑO</div>
          </div>
        </div>

        <nav className={styles.nav}>
          {GROUPS.map((g, gi) => (
            <div key={g.title} className={styles.navGroup}>
              <div className={styles.navGroupTitle}>{g.title}</div>
              {g.items.map(s => (
                <button
                  key={s.key}
                  className={`${styles.navItem} ${section === s.key ? styles.navActive : ''}`}
                  onClick={() => go(s.key)}
                >
                  {s.label}
                  {s.funcion && <EtiquetaPlan plan={plan} funcion={s.funcion} />}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <button className={styles.logoutBtn} style={{ marginBottom: 4 }} onClick={() => window.open('/ayuda', '_blank', 'noopener')}>
          AYUDA ↗
        </button>
        {webSlug && (
          <button className={styles.logoutBtn} style={{ color: 'var(--red-400)', marginBottom: 4 }} onClick={() => window.open(venueUrl(webSlug), '_blank', 'noopener')}>
            VER MI WEB ↗
          </button>
        )}
        {user?.isSuperadmin && (
          <button className={styles.logoutBtn} style={{ color: '#EC268F', marginBottom: 4 }} onClick={() => { window.location.href = '/superadmin'; }}>
            ★ SUPER-ADMIN
          </button>
        )}
        <button className={styles.logoutBtn} onClick={logout}>Cerrar sesión</button>
      </aside>

      {/* Main */}
      <main className={styles.main}>
        <div className={styles.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <button className={styles.menuToggle} onClick={() => setMenuOpen(o => !o)} aria-label="Menú">☰</button>
            <div className={styles.pageTitle}>
              {SECTIONS.find(s => s.key === section)?.label}
            </div>
          </div>
          <div className={styles.userChip}>{user?.name}</div>
        </div>

        <div className={styles.content}>
          <VerifyBanner api={api} />
          <TrialBanner />
          <ExpiredBanner />
          <ChargeBanner />
          <PriceNoticeBanner api={api} />
          {/* Una sección que el plan no incluye muestra por qué, en lugar de abrirse y fallar. */}
          {(() => { const f = SECTIONS.find(s => s.key === section)?.funcion; return f && !habilitada(plan, f); })()
            ? <PlanBloqueado plan={plan} funcion={SECTIONS.find(s => s.key === section).funcion} onVerPlanes={() => go('miplan')} />
            : (<>
          {section === 'eventos' && <EventsModule />}
          {section === 'analitica' && <Analitica />}
          {section === 'grupos' && <GruposRrpp />}
          {section === 'grupos-lotes' && <GruposLotes />}
          {section === 'cajas' && <TpvMenus />}
          {section === 'contable' && <Contable />}
          {section === 'pagos' && <IntegracionPagos />}
          {section === 'facturacion' && <Facturacion />}
          {section === 'inventario' && <Inventario />}
          {section === 'galeria' && <Galeria />}
          {section === 'sessions' && <Sessions />}
          {section === 'miweb' && <MiWeb />}
          {section === 'emailmkt' && <EmailMkt />}
          {section === 'puntos' && <Puntos />}
          {section === 'titular' && <DatosTitular />}
          {section === 'miplan' && <MiPlan onCambio={() => refrescarPlan(api)} />}
          {section === 'seguridad' && <Seguridad api={api} user={user} />}
            </>)}
        </div>
      </main>
    </div>
  );
}

// Seguridad de la cuenta: el propio dueño cambia SU email y SU contraseña. Se tipean acá
// (viajan del navegador al backend por HTTPS, no pasan por ningún intermediario ni log).
// Tras cambiar cualquiera de los dos hay que volver a entrar (el token viejo queda viejo).
function Seguridad({ api, user }) {
  const [email, setEmail] = useState(user?.email || '');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  async function saveEmail() {
    setEmailMsg('');
    const em = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setEmailMsg('⚠ Email inválido'); return; }
    if (em === (user?.email || '').toLowerCase()) { setEmailMsg('Es el mismo email actual.'); return; }
    if (!window.confirm(`Vas a cambiar tu email de acceso a:\n\n${em}\n\nDesde la próxima vez vas a entrar con ese email. ¿Confirmás?`)) return;
    setEmailBusy(true);
    try {
      await api.patch(`/api/users/${user.id}`, { email: em });
      setEmailMsg('✓ Email cambiado. Cerrá sesión y volvé a entrar con el nuevo email (misma contraseña).');
    } catch (e) { setEmailMsg('⚠ ' + (e.message || 'No se pudo cambiar')); } finally { setEmailBusy(false); }
  }

  async function savePw() {
    setPwMsg('');
    if (pw.length < 8) { setPwMsg('⚠ Usá al menos 8 caracteres (mejor 12+, con mayúsculas, números y símbolos).'); return; }
    if (pw !== pw2) { setPwMsg('⚠ Las dos contraseñas no coinciden.'); return; }
    setPwBusy(true);
    try {
      await api.patch(`/api/users/${user.id}`, { password: pw });
      setPw(''); setPw2('');
      setPwMsg('✓ Contraseña cambiada. Cerrá sesión y volvé a entrar con la nueva.');
    } catch (e) { setPwMsg('⚠ ' + (e.message || 'No se pudo cambiar')); } finally { setPwBusy(false); }
  }

  const card = { maxWidth: 460, marginBottom: 20 };
  return (
    <div>
      <div className="lb-card" style={{ ...card, padding: '22px 24px' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 4 }}>Email de acceso</div>
        <p style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 6, lineHeight: 1.5 }}>Con este email entrás al panel. Si lo cambiás, la próxima vez ingresás con el nuevo (la contraseña no cambia).</p>
        <input className="lb-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" style={{ marginTop: 6 }} />
        <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ marginTop: 12 }} disabled={emailBusy} onClick={saveEmail}>{emailBusy ? '…' : 'Cambiar email'}</button>
        {emailMsg && <div style={{ marginTop: 10, fontSize: 12.5, color: emailMsg[0] === '✓' ? '#2ecc71' : 'var(--ink-700)' }}>{emailMsg}</div>}
      </div>

      <div className="lb-card" style={{ ...card, padding: '22px 24px' }}>
        <div className="lb-eyebrow" style={{ marginBottom: 4 }}>Contraseña</div>
        <p style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 6, lineHeight: 1.5 }}>Elegí una contraseña fuerte. La escribís acá y viaja cifrada al servidor; nadie más la ve.</p>
        <input className="lb-input" type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Nueva contraseña" autoComplete="new-password" style={{ marginTop: 6 }} />
        <input className="lb-input" type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Repetir la nueva contraseña" autoComplete="new-password" style={{ marginTop: 10 }} />
        <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ marginTop: 12 }} disabled={pwBusy} onClick={savePw}>{pwBusy ? '…' : 'Cambiar contraseña'}</button>
        {pwMsg && <div style={{ marginTop: 10, fontSize: 12.5, color: pwMsg[0] === '✓' ? '#2ecc71' : 'var(--ink-700)' }}>{pwMsg}</div>}
      </div>
    </div>
  );
}
